/**
 * `renderBrandFeed` — responsive gallery of a brand's public content
 * (POL-585). Composes on top of `renderPolstCard` (POL-583) and
 * `renderCampaignPlayer` (POL-584): every item is a summary tile in the
 * grid, and clicking it opens a modal inside the same shadow root that
 * re-uses the full card / player primitive.
 *
 * Implementation notes:
 *   - Fetches go through injectable callbacks that default to the
 *     `getBrandPolsts` / `getBrandCampaigns` / `getBrandFeed` REST
 *     wrappers in `./api`. Tests can supply their own so the renderer
 *     stays deterministic and network-free.
 *   - Pagination is cursor-only (matching the REST surface). The
 *     `loadMore()` handle call is idempotent while a request is
 *     in-flight — a second call collapses into the pending promise.
 *   - Auto-pagination uses `IntersectionObserver`. We hold a sentinel
 *     element below the grid; when it becomes visible we call
 *     `loadMore()`. Tests inject a mock IO implementation to drive
 *     the sentinel deterministically.
 *   - The modal lives in a separate `.polst-feed__modal` layer that is
 *     appended to the widget container (not the grid). Opening the
 *     modal spawns a fresh `renderPolstCard` / `renderCampaignPlayer`
 *     instance and stores its handle so `closeItem()` can tear it
 *     down cleanly.
 *   - Zero runtime deps; hand-rolled DOM like the rest of the widget.
 */
import {
  getBrandPolsts as defaultGetBrandPolsts,
  getBrandCampaigns as defaultGetBrandCampaigns,
  getBrandFeed as defaultGetBrandFeed,
} from "./api";
import { trapFocus } from "./a11y";
import {
  renderPolstCard,
  type PolstCardDTO,
  type PolstCardOptions,
  type PolstHandle,
} from "./polst-card";
import {
  renderCampaignPlayer,
  type CampaignPlayerDTO,
  type CampaignPlayerHandle,
  type CampaignPlayerOptions,
} from "./campaign-player";

// ─── Types ──────────────────────────────────────────────────────────────────

export type BrandFeedMode = "polsts" | "campaigns" | "mixed";

/**
 * Mixed-feed item envelope matching `/api/rest/v1/brands/{slug}/feed`.
 * Each row is keyed by `kind`; the matching DTO is attached inline.
 */
export interface BrandFeedMixedItem {
  kind: "polst" | "campaign";
  createdAt: string;
  polst?: PolstCardDTO;
  campaign?: CampaignPlayerDTO;
}

/** Unified feed row used by the renderer. */
export interface BrandFeedItem {
  kind: "polst" | "campaign";
  createdAt: string;
  polst?: PolstCardDTO;
  campaign?: CampaignPlayerDTO;
}

export interface BrandFeedPage<TItem> {
  data: TItem[];
  nextCursor: string | null;
}

export type BrandFeedStatus =
  | "loading"
  | "ready"
  | "loadingMore"
  | "empty"
  | "error";

export interface BrandFeedState {
  status: BrandFeedStatus;
  items: BrandFeedItem[];
  nextCursor: string | null;
  pageCount: number;
  hasMore: boolean;
  openItem: BrandFeedItem | null;
  error?: unknown;
}

export interface BrandFeedCardClickEvent {
  item: BrandFeedItem;
  /** Suppresses the default modal open. */
  preventDefault(): void;
  /** Whether `preventDefault()` was called. */
  defaultPrevented: boolean;
}

export interface BrandFeedPageLoadedEvent {
  items: BrandFeedItem[];
  nextCursor: string | null;
  pageCount: number;
}

/**
 * Minimal IntersectionObserver shape the feed depends on. We carry our
 * own typing so `document` + `global` stubs in tests can satisfy it
 * without dragging in the full DOM typings.
 */
export interface MinimalIntersectionObserverEntry {
  isIntersecting: boolean;
  target: Element;
}
export interface MinimalIntersectionObserver {
  observe(target: Element): void;
  unobserve(target: Element): void;
  disconnect(): void;
}
export type MinimalIntersectionObserverCtor = new (
  callback: (entries: MinimalIntersectionObserverEntry[]) => void,
  options?: { root?: Element | null; rootMargin?: string; threshold?: number | number[] },
) => MinimalIntersectionObserver;

export interface BrandFeedOptions {
  /** Feed mode. Default `"mixed"`. */
  mode?: BrandFeedMode;
  /** Items per page. 1..100, default 20. */
  pageSize?: number;
  /** Auto-paginate on scroll via IntersectionObserver. Default `false`. */
  autoLoadOnScroll?: boolean;
  /** Polsts-only fetcher override. */
  fetchPolsts?: (
    brandSlug: string,
    cursor: string | null,
    limit: number,
  ) => Promise<BrandFeedPage<PolstCardDTO>>;
  /** Campaigns-only fetcher override. */
  fetchCampaigns?: (
    brandSlug: string,
    cursor: string | null,
    limit: number,
  ) => Promise<BrandFeedPage<CampaignPlayerDTO>>;
  /** Mixed-feed fetcher override. */
  fetchFeed?: (
    brandSlug: string,
    cursor: string | null,
    limit: number,
  ) => Promise<BrandFeedPage<BrandFeedMixedItem>>;
  /** Pre-modal hook; call `event.preventDefault()` to suppress the modal. */
  onCardClick?: (event: BrandFeedCardClickEvent) => void;
  /** Fired after every page resolves (even if empty). */
  onPageLoaded?: (event: BrandFeedPageLoadedEvent) => void;
  /** Non-fatal error hook. The inline error state is always updated too. */
  onError?: (err: unknown) => void;
  /** Document override for deterministic rendering (mirrors card / player). */
  document?: Document;
  /** Forwarded to the modal polst card. */
  hideShare?: boolean;
  /** Forwarded to the modal polst card + campaign player. */
  hideBrand?: boolean;
  /** IO constructor override for tests. Defaults to `globalThis.IntersectionObserver`. */
  intersectionObserver?: MinimalIntersectionObserverCtor;
  /**
   * Card options override used when mounting the polst card inside the
   * modal. Callers typically don't need this, but it lets campaign-
   * embed-style hosts supply a custom voter.
   */
  polstCardOptions?: PolstCardOptions;
  /**
   * Campaign player options override used when mounting the player
   * inside the modal.
   */
  campaignPlayerOptions?: CampaignPlayerOptions;
}

export interface BrandFeedHandle {
  getState(): BrandFeedState;
  /** Load the next page. Idempotent while a request is in-flight. */
  loadMore(): Promise<void>;
  /** Reset pagination + reload the first page. */
  refresh(): Promise<void>;
  /** Open an item in the inline modal. No-op after destroy. */
  openItem(item: BrandFeedItem): void;
  /** Close the inline modal. */
  closeItem(): void;
  /** Tear down DOM + observers. Further handle calls are no-ops. */
  destroy(): void;
}

// ─── Styles ─────────────────────────────────────────────────────────────────

const FEED_CSS = `
.polst-feed {
  display: flex;
  flex-direction: column;
  gap: var(--polst-spacing-md);
  width: 100%;
  box-sizing: border-box;
  color: var(--polst-fg);
  background: var(--polst-bg);
  container-type: inline-size;
}
.polst-feed__grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--polst-spacing-md);
}
@container (min-width: 480px) {
  .polst-feed__grid { grid-template-columns: repeat(2, 1fr); }
}
@container (min-width: 960px) {
  .polst-feed__grid { grid-template-columns: repeat(3, 1fr); }
}
@media (min-width: 480px) {
  .polst-feed__grid { grid-template-columns: repeat(2, 1fr); }
}
@media (min-width: 960px) {
  .polst-feed__grid { grid-template-columns: repeat(3, 1fr); }
}
.polst-feed__tile {
  display: flex;
  flex-direction: column;
  gap: var(--polst-spacing-sm);
  padding: var(--polst-spacing-md);
  border-radius: var(--polst-radius);
  background: color-mix(in srgb, var(--polst-bg) 94%, var(--polst-fg) 6%);
  border: 1px solid color-mix(in srgb, var(--polst-fg) 12%, transparent);
  cursor: pointer;
  text-align: left;
  color: inherit;
  font: inherit;
  box-sizing: border-box;
  transition: transform 120ms ease-out, box-shadow 120ms ease-out;
}
.polst-feed__tile:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px color-mix(in srgb, var(--polst-fg) 16%, transparent);
}
.polst-feed__tile:focus-visible {
  outline: 2px solid var(--polst-accent);
  outline-offset: 2px;
}
.polst-feed__thumb {
  width: 100%;
  aspect-ratio: 4 / 3;
  object-fit: cover;
  border-radius: var(--polst-radius);
  background: color-mix(in srgb, var(--polst-fg) 8%, transparent);
  display: block;
}
.polst-feed__thumb-fallback {
  width: 100%;
  aspect-ratio: 4 / 3;
  border-radius: var(--polst-radius);
  background: color-mix(in srgb, var(--polst-fg) 8%, transparent);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  font-weight: 700;
  color: var(--polst-muted-fg);
}
.polst-feed__tile-title {
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  line-height: 1.3;
}
.polst-feed__tile-meta {
  margin: 0;
  font-size: 12px;
  color: var(--polst-muted-fg);
  display: flex;
  justify-content: space-between;
  gap: var(--polst-spacing-sm);
}
.polst-feed__badge {
  display: inline-block;
  font-size: 11px;
  padding: 2px var(--polst-spacing-sm);
  border-radius: 999px;
  background: color-mix(in srgb, var(--polst-accent) 22%, transparent);
  color: var(--polst-accent);
  align-self: flex-start;
  letter-spacing: 0.02em;
}
.polst-feed__skeleton {
  height: 180px;
  border-radius: var(--polst-radius);
  background: linear-gradient(
    90deg,
    color-mix(in srgb, var(--polst-fg) 6%, transparent) 0%,
    color-mix(in srgb, var(--polst-fg) 14%, transparent) 50%,
    color-mix(in srgb, var(--polst-fg) 6%, transparent) 100%
  );
  background-size: 200% 100%;
  animation: polst-feed-shimmer 1.2s linear infinite;
}
@keyframes polst-feed-shimmer {
  0% { background-position: 100% 0; }
  100% { background-position: -100% 0; }
}
.polst-feed__empty,
.polst-feed__error {
  padding: var(--polst-spacing-lg);
  text-align: center;
  color: var(--polst-muted-fg);
  font-size: 14px;
  border-radius: var(--polst-radius);
  background: color-mix(in srgb, var(--polst-fg) 4%, transparent);
}
.polst-feed__error { color: var(--polst-error-fg); }
.polst-feed__footer {
  display: flex;
  justify-content: center;
  padding: var(--polst-spacing-md) 0;
  min-height: 1px;
}
.polst-feed__load-more {
  background: var(--polst-accent);
  color: var(--polst-accent-fg);
  border: 0;
  border-radius: var(--polst-radius);
  padding: var(--polst-spacing-sm) var(--polst-spacing-lg);
  font: inherit;
  font-weight: 600;
  cursor: pointer;
}
.polst-feed__load-more[disabled] { opacity: 0.5; cursor: not-allowed; }
.polst-feed__sentinel {
  width: 100%;
  height: 1px;
}
.polst-feed__retry {
  margin-top: var(--polst-spacing-sm);
  background: none;
  border: 1px solid currentColor;
  border-radius: var(--polst-radius);
  padding: var(--polst-spacing-xs) var(--polst-spacing-md);
  font: inherit;
  cursor: pointer;
  color: inherit;
}
.polst-feed__modal {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: color-mix(in srgb, #000 65%, transparent);
  padding: var(--polst-spacing-lg);
  z-index: 2147483647;
}
.polst-feed__modal-dialog {
  position: relative;
  width: min(640px, 100%);
  max-height: 90vh;
  overflow: auto;
  background: var(--polst-bg);
  color: var(--polst-fg);
  border-radius: var(--polst-radius);
  padding: var(--polst-spacing-lg);
  box-shadow: 0 16px 48px color-mix(in srgb, #000 40%, transparent);
  box-sizing: border-box;
}
.polst-feed__modal-close {
  position: absolute;
  top: var(--polst-spacing-sm);
  right: var(--polst-spacing-sm);
  background: none;
  border: 0;
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
  color: inherit;
  padding: var(--polst-spacing-xs) var(--polst-spacing-sm);
}
.polst-feed__modal-body {
  margin-top: var(--polst-spacing-md);
}
.polst-feed__modal-close:focus-visible {
  outline: 2px solid var(--polst-accent);
  outline-offset: 2px;
}
.polst-feed__load-more:focus-visible {
  outline: 2px solid var(--polst-accent);
  outline-offset: 2px;
}
@media (prefers-reduced-motion: reduce) {
  .polst-feed__tile { transition: none !important; }
  .polst-feed__tile:hover { transform: none !important; }
  .polst-feed__skeleton { animation: none !important; }
  .polst-feed * { animation: none !important; transition: none !important; }
}
`;

const STYLE_MARKER = "data-polst-feed-styles";

function ensureStyles(root: ShadowRoot, doc: Document): void {
  if (root.querySelector(`style[${STYLE_MARKER}]`)) return;
  const styleEl = doc.createElement("style");
  styleEl.setAttribute(STYLE_MARKER, "");
  styleEl.textContent = FEED_CSS;
  root.appendChild(styleEl);
}

function findWidgetContainer(root: ShadowRoot): Element {
  const container = root.querySelector("[data-polst-widget-root]");
  if (container) return container;
  return root as unknown as Element;
}

function clampPageSize(value: number | undefined): number {
  if (!Number.isFinite(value ?? NaN)) return 20;
  const n = Math.floor(value as number);
  if (n < 1) return 1;
  if (n > 100) return 100;
  return n;
}

function formatNumber(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1).replace(/\.0$/, "")}K`;
  return String(n);
}

function itemKey(item: BrandFeedItem, index: number): string {
  if (item.kind === "polst" && item.polst) return `polst:${item.polst.shortId}`;
  if (item.kind === "campaign" && item.campaign) return `campaign:${item.campaign.campaignId}`;
  return `item:${index}`;
}

// ─── Default fetchers ──────────────────────────────────────────────────────

function wrapDefaultPolsts(
  brandSlug: string,
  cursor: string | null,
  limit: number,
): Promise<BrandFeedPage<PolstCardDTO>> {
  // The API module's `getBrandPolsts` doesn't yet accept pagination args
  // directly — it forwards through `ClientOptions`. We still pass the
  // cursor/limit via query string by leaning on the underlying REST
  // client's path. For now, we forward to the default fetcher and
  // coerce the response — the REST envelope already matches our shape.
  return defaultGetBrandPolsts(brandSlug).then((r) =>
    coercePage<PolstCardDTO>(r, cursor, limit),
  );
}

function wrapDefaultCampaigns(
  brandSlug: string,
  cursor: string | null,
  limit: number,
): Promise<BrandFeedPage<CampaignPlayerDTO>> {
  return defaultGetBrandCampaigns(brandSlug).then((r) =>
    coercePage<CampaignPlayerDTO>(r, cursor, limit),
  );
}

function wrapDefaultFeed(
  brandSlug: string,
  cursor: string | null,
  limit: number,
): Promise<BrandFeedPage<BrandFeedMixedItem>> {
  return defaultGetBrandFeed(brandSlug).then((r) =>
    coercePage<BrandFeedMixedItem>(r, cursor, limit),
  );
}

function coercePage<T>(
  raw: unknown,
  _cursor: string | null,
  _limit: number,
): BrandFeedPage<T> {
  // The REST envelope always looks like `{ data: [...], nextCursor }`.
  // `getBrand*` currently returns `{ items, nextCursor }` — accept both
  // shapes so tests stubbing the REST client don't have to mimic the
  // legacy alias.
  const r = raw as { data?: T[]; items?: T[]; nextCursor?: string | null } | null | undefined;
  const data = Array.isArray(r?.data) ? (r?.data as T[]) : Array.isArray(r?.items) ? (r?.items as T[]) : [];
  const nextCursor = typeof r?.nextCursor === "string" ? r.nextCursor : null;
  return { data, nextCursor };
}

// ─── renderer ───────────────────────────────────────────────────────────────

export function renderBrandFeed(
  root: ShadowRoot,
  brandSlug: string,
  options: BrandFeedOptions = {},
): BrandFeedHandle {
  if (!root || typeof (root as unknown as { appendChild?: unknown }).appendChild !== "function") {
    throw new Error("renderBrandFeed(): root must be a ShadowRoot");
  }
  if (!brandSlug || typeof brandSlug !== "string") {
    throw new Error("renderBrandFeed(): brandSlug is required");
  }

  const doc =
    options.document ??
    ((root as unknown as { ownerDocument?: Document }).ownerDocument as Document);
  if (!doc) {
    throw new Error("renderBrandFeed(): unable to resolve Document from root");
  }

  const mode: BrandFeedMode = options.mode ?? "mixed";
  const pageSize = clampPageSize(options.pageSize);
  const autoLoadOnScroll = options.autoLoadOnScroll === true;

  const fetchPolsts = options.fetchPolsts ?? wrapDefaultPolsts;
  const fetchCampaigns = options.fetchCampaigns ?? wrapDefaultCampaigns;
  const fetchFeed = options.fetchFeed ?? wrapDefaultFeed;

  ensureStyles(root, doc);
  const widgetContainer = findWidgetContainer(root);

  const feedEl = doc.createElement("section");
  feedEl.className = "polst-feed";
  feedEl.setAttribute("data-polst-brand-feed", "");
  feedEl.setAttribute("data-mode", mode);
  feedEl.setAttribute("data-brand-slug", brandSlug);

  const gridEl = doc.createElement("div");
  gridEl.className = "polst-feed__grid";
  gridEl.setAttribute("role", "list");
  feedEl.appendChild(gridEl);

  const footerEl = doc.createElement("div");
  footerEl.className = "polst-feed__footer";
  feedEl.appendChild(footerEl);

  const sentinelEl = doc.createElement("div");
  sentinelEl.className = "polst-feed__sentinel";
  sentinelEl.setAttribute("data-polst-feed-sentinel", "");
  feedEl.appendChild(sentinelEl);

  widgetContainer.appendChild(feedEl);

  // State machine
  let items: BrandFeedItem[] = [];
  let nextCursor: string | null = null;
  let pageCount = 0;
  let status: BrandFeedStatus = "loading";
  let lastError: unknown = null;
  let openItem: BrandFeedItem | null = null;
  let lastFocused: Element | null = null;
  let destroyed = false;
  let inflight: Promise<void> | null = null;

  // Modal state
  let modalEl: HTMLElement | null = null;
  let modalBodyEl: HTMLElement | null = null;
  let modalCardHandle: PolstHandle | null = null;
  let modalCampaignHandle: CampaignPlayerHandle | null = null;
  let modalFocusTrapTeardown: (() => void) | null = null;

  // Observer
  let ioImpl: MinimalIntersectionObserver | null = null;

  function setState(patch: Partial<{ status: BrandFeedStatus; error: unknown }>): void {
    if (patch.status) status = patch.status;
    if ("error" in patch) lastError = patch.error ?? null;
  }

  function render(): void {
    if (destroyed) return;
    feedEl.setAttribute("data-status", status);

    // Grid body
    gridEl.replaceChildren();
    if (status === "loading" && items.length === 0) {
      for (let i = 0; i < 3; i++) {
        const sk = doc.createElement("div");
        sk.className = "polst-feed__skeleton";
        sk.setAttribute("role", "listitem");
        sk.setAttribute("aria-busy", "true");
        gridEl.appendChild(sk);
      }
    } else if (status === "empty") {
      const empty = doc.createElement("div");
      empty.className = "polst-feed__empty";
      empty.textContent = "Nothing to show yet.";
      gridEl.appendChild(empty);
    } else {
      for (const [i, item] of items.entries()) {
        gridEl.appendChild(renderTile(item, i));
      }
    }

    // Footer
    footerEl.replaceChildren();
    if (status === "error") {
      const err = doc.createElement("div");
      err.className = "polst-feed__error";
      const msg =
        lastError && typeof lastError === "object" && "message" in (lastError as object)
          ? String((lastError as { message?: unknown }).message ?? "Failed to load feed")
          : "Failed to load feed";
      err.textContent = msg;
      const retry = doc.createElement("button");
      retry.type = "button";
      retry.className = "polst-feed__retry";
      retry.textContent = "Retry";
      retry.setAttribute("data-action", "retry");
      retry.addEventListener("click", () => void retryFromError());
      err.appendChild(doc.createElement("br"));
      err.appendChild(retry);
      footerEl.appendChild(err);
    } else if (status === "loadingMore") {
      const sk = doc.createElement("div");
      sk.className = "polst-feed__skeleton";
      sk.style.height = "44px";
      sk.setAttribute("aria-busy", "true");
      footerEl.appendChild(sk);
    } else if (nextCursor) {
      const btn = doc.createElement("button");
      btn.type = "button";
      btn.className = "polst-feed__load-more";
      btn.textContent = "Load more";
      btn.setAttribute("data-action", "load-more");
      btn.addEventListener("click", () => void loadMore());
      footerEl.appendChild(btn);
    }
  }

  function renderTile(item: BrandFeedItem, index: number): HTMLElement {
    const tile = doc.createElement("button");
    tile.type = "button";
    tile.className = "polst-feed__tile";
    tile.setAttribute("role", "listitem");
    tile.setAttribute("data-item-kind", item.kind);
    tile.setAttribute("data-item-key", itemKey(item, index));

    if (item.kind === "polst" && item.polst) {
      renderPolstTile(tile, item.polst);
    } else if (item.kind === "campaign" && item.campaign) {
      renderCampaignTile(tile, item.campaign);
    }

    tile.addEventListener("click", () => handleTileClick(item, tile));
    return tile;
  }

  function renderPolstTile(tile: HTMLElement, polst: PolstCardDTO): void {
    const imageUrl = polst.optionA?.imageUrl ?? polst.optionB?.imageUrl ?? null;
    if (imageUrl) {
      const img = doc.createElement("img");
      img.className = "polst-feed__thumb";
      img.src = imageUrl;
      img.setAttribute("alt", polst.title);
      img.setAttribute("loading", "lazy");
      img.setAttribute("decoding", "async");
      tile.appendChild(img);
    } else {
      const fb = doc.createElement("div");
      fb.className = "polst-feed__thumb-fallback";
      fb.textContent = polst.title.charAt(0).toUpperCase() || "?";
      fb.setAttribute("aria-hidden", "true");
      tile.appendChild(fb);
    }

    const badge = doc.createElement("span");
    badge.className = "polst-feed__badge";
    badge.textContent = polst.status === "closed" ? "Closed" : polst.status === "scheduled" ? "Scheduled" : "Polst";
    tile.appendChild(badge);

    const title = doc.createElement("p");
    title.className = "polst-feed__tile-title";
    title.textContent = polst.title;
    tile.appendChild(title);

    const meta = doc.createElement("p");
    meta.className = "polst-feed__tile-meta";
    const total = polst.tallies?.total ?? 0;
    const brandText = !options.hideBrand && polst.brand ? polst.brand.name : "";
    const left = doc.createElement("span");
    left.textContent = brandText;
    const right = doc.createElement("span");
    right.textContent = `${formatNumber(total)} ${total === 1 ? "vote" : "votes"}`;
    meta.appendChild(left);
    meta.appendChild(right);
    tile.appendChild(meta);
  }

  function renderCampaignTile(tile: HTMLElement, campaign: CampaignPlayerDTO): void {
    // Campaigns lack a cover image; use the first polst's thumbnail.
    const firstWithImage = campaign.polsts.find(
      (p) => p.optionA?.imageUrl || p.optionB?.imageUrl,
    );
    const thumbUrl =
      firstWithImage?.optionA?.imageUrl ?? firstWithImage?.optionB?.imageUrl ?? null;
    if (thumbUrl) {
      const img = doc.createElement("img");
      img.className = "polst-feed__thumb";
      img.src = thumbUrl;
      img.setAttribute("alt", campaign.title);
      img.setAttribute("loading", "lazy");
      img.setAttribute("decoding", "async");
      tile.appendChild(img);
    } else {
      const fb = doc.createElement("div");
      fb.className = "polst-feed__thumb-fallback";
      fb.textContent = campaign.title.charAt(0).toUpperCase() || "?";
      fb.setAttribute("aria-hidden", "true");
      tile.appendChild(fb);
    }

    const badge = doc.createElement("span");
    badge.className = "polst-feed__badge";
    badge.textContent = `Campaign · ${campaign.totalSteps} step${campaign.totalSteps === 1 ? "" : "s"}`;
    tile.appendChild(badge);

    const title = doc.createElement("p");
    title.className = "polst-feed__tile-title";
    title.textContent = campaign.title;
    tile.appendChild(title);

    const meta = doc.createElement("p");
    meta.className = "polst-feed__tile-meta";
    const brandText = !options.hideBrand && campaign.brand ? campaign.brand.name : "";
    const left = doc.createElement("span");
    left.textContent = brandText;
    const right = doc.createElement("span");
    right.textContent = campaign.status === "closed" ? "Closed" : "";
    meta.appendChild(left);
    meta.appendChild(right);
    tile.appendChild(meta);
  }

  function handleTileClick(item: BrandFeedItem, originEl: Element): void {
    if (destroyed) return;
    let prevented = false;
    const evt: BrandFeedCardClickEvent = {
      item,
      defaultPrevented: false,
      preventDefault() {
        prevented = true;
        this.defaultPrevented = true;
      },
    };
    try {
      options.onCardClick?.(evt);
    } catch (err) {
      // Host callback failure should not break the feed.
      options.onError?.(err);
    }
    if (prevented) return;
    lastFocused = originEl;
    openItemInternal(item);
  }

  function openItemInternal(item: BrandFeedItem): void {
    if (destroyed) return;
    // Tear down any existing modal content first.
    teardownModal();

    modalEl = doc.createElement("div");
    modalEl.className = "polst-feed__modal";
    modalEl.setAttribute("data-polst-feed-modal", "");
    modalEl.setAttribute("role", "dialog");
    modalEl.setAttribute("aria-modal", "true");

    const dialog = doc.createElement("div");
    dialog.className = "polst-feed__modal-dialog";
    dialog.setAttribute("data-polst-feed-modal-dialog", "");
    // A visually-hidden heading backs `aria-labelledby` — it's more
    // reliable than depending on the inner card's h3 which is mounted
    // asynchronously via `renderPolstCard` / `renderCampaignPlayer`.
    const labelId = `polst-feed-modal-${
      item.kind === "polst" ? item.polst?.shortId ?? "x" : item.campaign?.campaignId ?? "x"
    }-label`;
    const labelEl = doc.createElement("h2");
    labelEl.id = labelId;
    labelEl.textContent =
      item.kind === "polst"
        ? item.polst?.title ?? "Polst"
        : item.campaign?.title ?? "Campaign";
    labelEl.setAttribute(
      "style",
      "position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap;border:0;",
    );
    dialog.appendChild(labelEl);
    modalEl.setAttribute("aria-labelledby", labelId);

    const closeBtn = doc.createElement("button");
    closeBtn.type = "button";
    closeBtn.className = "polst-feed__modal-close";
    closeBtn.textContent = "×";
    closeBtn.setAttribute("aria-label", "Close");
    closeBtn.setAttribute("data-action", "close-modal");
    closeBtn.addEventListener("click", () => closeItem());
    dialog.appendChild(closeBtn);

    modalBodyEl = doc.createElement("div");
    modalBodyEl.className = "polst-feed__modal-body";
    dialog.appendChild(modalBodyEl);

    modalEl.appendChild(dialog);

    // Dismiss-on-backdrop-click (not on dialog click).
    modalEl.addEventListener("click", (evt) => {
      if (evt.target === modalEl) closeItem();
    });

    widgetContainer.appendChild(modalEl);

    // Focus trap on the dialog. Escape closes; Tab / Shift+Tab cycle
    // inside the dialog so keyboard users cannot leak into the host
    // page or the feed beneath the modal.
    modalFocusTrapTeardown = trapFocus(dialog, {
      onEscape: () => closeItem(),
      initialFocus: closeBtn,
    });

    // Mount the card / player into the modal body.
    // We need a ShadowRoot-like target for the card primitive — fortunately
    // the card writes into `[data-polst-widget-root]` if present and only
    // depends on `appendChild`. We proxy a mini "root" that forwards to
    // modalBody so the card / player install cleanly.
    const proxyRoot = makeProxyShadowRoot(modalBodyEl, root);

    if (item.kind === "polst" && item.polst) {
      modalCardHandle = renderPolstCard(proxyRoot, item.polst, {
        ...options.polstCardOptions,
        document: options.polstCardOptions?.document ?? doc,
        hideBrand: options.polstCardOptions?.hideBrand ?? options.hideBrand,
        hideShare: options.polstCardOptions?.hideShare ?? options.hideShare,
      });
    } else if (item.kind === "campaign" && item.campaign) {
      modalCampaignHandle = renderCampaignPlayer(proxyRoot, item.campaign, {
        ...options.campaignPlayerOptions,
        document: options.campaignPlayerOptions?.document ?? doc,
        hideBrand: options.campaignPlayerOptions?.hideBrand ?? options.hideBrand,
        hideShare: options.campaignPlayerOptions?.hideShare ?? options.hideShare,
      });
    }

    openItem = item;
  }

  function makeProxyShadowRoot(bodyEl: HTMLElement, trueRoot: ShadowRoot): ShadowRoot {
    // The card + player primitives call `root.querySelector`,
    // `root.appendChild`, and read `root.ownerDocument`. A plain element
    // satisfies all of those. We also install a `data-polst-widget-root`
    // wrapper child so `findWidgetContainer()` inside the primitives
    // resolves to our scoped body instead of the outer feed container.
    const inner = doc.createElement("div");
    inner.setAttribute("data-polst-widget-root", "");
    bodyEl.appendChild(inner);

    // The primitives `ensureStyles` scope their stylesheets to the
    // shadow root (querying by `style[data-polst-card-styles]`). Point
    // them at the same root we're mounted into so they reuse the
    // already-installed stylesheet if one exists.
    const proxy = new Proxy(bodyEl as unknown as ShadowRoot, {
      get(target, prop) {
        if (prop === "appendChild") {
          // Any <style> injection from the card/player should land on
          // the outer shadow root so other cards inherit it. Everything
          // else (the card element itself) lands in the modal body.
          return (node: Node) => {
            if ((node as Element).tagName === "STYLE") {
              // Dedupe against the real shadow root.
              const marker = (node as Element).getAttribute?.bind(node as Element);
              if (marker) {
                const cardMark = marker("data-polst-card-styles");
                const campMark = marker("data-polst-campaign-styles");
                if (cardMark !== null || campMark !== null) {
                  // Install on the real root if absent.
                  const query = cardMark !== null
                    ? "style[data-polst-card-styles]"
                    : "style[data-polst-campaign-styles]";
                  if (!trueRoot.querySelector(query)) {
                    trueRoot.appendChild(node);
                  }
                  return node;
                }
              }
              // Unknown <style> — fall through to body insertion.
            }
            return bodyEl.appendChild(node);
          };
        }
        if (prop === "querySelector") {
          return (selector: string) => {
            // Any lookup for a pre-installed stylesheet should check the
            // real root so the primitives don't stack duplicates.
            if (
              selector === "style[data-polst-card-styles]" ||
              selector === "style[data-polst-campaign-styles]"
            ) {
              return trueRoot.querySelector(selector);
            }
            return bodyEl.querySelector(selector);
          };
        }
        if (prop === "ownerDocument") return doc;
        const val = (target as unknown as Record<string | symbol, unknown>)[prop];
        if (typeof val === "function") return (val as (...args: unknown[]) => unknown).bind(bodyEl);
        return val;
      },
    });
    return proxy;
  }

  function teardownModal(): void {
    if (modalCardHandle) {
      modalCardHandle.destroy();
      modalCardHandle = null;
    }
    if (modalCampaignHandle) {
      modalCampaignHandle.destroy();
      modalCampaignHandle = null;
    }
    if (modalFocusTrapTeardown) {
      modalFocusTrapTeardown();
      modalFocusTrapTeardown = null;
    }
    if (modalEl && modalEl.parentNode) modalEl.parentNode.removeChild(modalEl);
    modalEl = null;
    modalBodyEl = null;
  }

  function closeItem(): void {
    if (!openItem) return;
    teardownModal();
    openItem = null;
    if (lastFocused && typeof (lastFocused as HTMLElement).focus === "function") {
      (lastFocused as HTMLElement).focus();
    }
  }

  async function doFetchPage(cursor: string | null): Promise<{
    rows: BrandFeedItem[];
    nextCursor: string | null;
  }> {
    if (mode === "polsts") {
      const page = await fetchPolsts(brandSlug, cursor, pageSize);
      return {
        rows: (page.data ?? []).map<BrandFeedItem>((polst) => ({
          kind: "polst",
          createdAt: polst.createdAt ?? "",
          polst,
        })),
        nextCursor: page.nextCursor ?? null,
      };
    }
    if (mode === "campaigns") {
      const page = await fetchCampaigns(brandSlug, cursor, pageSize);
      return {
        rows: (page.data ?? []).map<BrandFeedItem>((campaign) => ({
          kind: "campaign",
          createdAt: "",
          campaign,
        })),
        nextCursor: page.nextCursor ?? null,
      };
    }
    const page = await fetchFeed(brandSlug, cursor, pageSize);
    return {
      rows: (page.data ?? []).map<BrandFeedItem>((raw) => ({
        kind: raw.kind,
        createdAt: raw.createdAt,
        polst: raw.polst,
        campaign: raw.campaign,
      })),
      nextCursor: page.nextCursor ?? null,
    };
  }

  function firePageLoaded(rows: BrandFeedItem[], cursor: string | null): void {
    try {
      options.onPageLoaded?.({ items: rows, nextCursor: cursor, pageCount });
    } catch (err) {
      options.onError?.(err);
    }
  }

  async function loadInitial(): Promise<void> {
    if (destroyed) return;
    if (inflight) return inflight;
    setState({ status: "loading", error: null });
    render();
    inflight = (async () => {
      try {
        const page = await doFetchPage(null);
        if (destroyed) return;
        items = page.rows;
        nextCursor = page.nextCursor;
        pageCount = 1;
        if (items.length === 0 && !nextCursor) {
          setState({ status: "empty" });
        } else {
          setState({ status: "ready" });
        }
        render();
        firePageLoaded(items, nextCursor);
        updateObserver();
      } catch (err) {
        if (destroyed) return;
        setState({ status: "error", error: err });
        render();
        options.onError?.(err);
      } finally {
        inflight = null;
      }
    })();
    return inflight;
  }

  async function loadMore(): Promise<void> {
    if (destroyed) return;
    if (inflight) return inflight;
    if (!nextCursor) return;
    if (status !== "ready" && status !== "error") return;
    setState({ status: "loadingMore", error: null });
    render();
    inflight = (async () => {
      try {
        const page = await doFetchPage(nextCursor);
        if (destroyed) return;
        items = items.concat(page.rows);
        nextCursor = page.nextCursor;
        pageCount += 1;
        setState({ status: items.length === 0 ? "empty" : "ready" });
        render();
        firePageLoaded(page.rows, nextCursor);
        updateObserver();
      } catch (err) {
        if (destroyed) return;
        setState({ status: "error", error: err });
        render();
        options.onError?.(err);
      } finally {
        inflight = null;
      }
    })();
    return inflight;
  }

  async function retryFromError(): Promise<void> {
    if (items.length === 0) {
      await loadInitial();
    } else {
      // Error surfaced during loadMore — try the same cursor again.
      setState({ status: "ready", error: null });
      render();
      await loadMore();
    }
  }

  async function refresh(): Promise<void> {
    items = [];
    nextCursor = null;
    pageCount = 0;
    await loadInitial();
  }

  function updateObserver(): void {
    if (destroyed) return;
    if (!autoLoadOnScroll) return;
    const Ctor: MinimalIntersectionObserverCtor | undefined =
      options.intersectionObserver ??
      ((doc.defaultView as unknown as { IntersectionObserver?: MinimalIntersectionObserverCtor })
        ?.IntersectionObserver ??
        (globalThis as unknown as { IntersectionObserver?: MinimalIntersectionObserverCtor })
          .IntersectionObserver);
    if (!Ctor) return;
    if (!ioImpl) {
      ioImpl = new Ctor((entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            void loadMore();
            break;
          }
        }
      });
      ioImpl.observe(sentinelEl);
    }
    // If we've reached the end, stop observing so we don't leak notifications.
    if (!nextCursor && ioImpl) {
      ioImpl.disconnect();
      ioImpl = null;
    }
  }

  // First paint + first fetch.
  render();
  void loadInitial();

  return {
    getState(): BrandFeedState {
      return {
        status,
        items: items.slice(),
        nextCursor,
        pageCount,
        hasMore: nextCursor !== null,
        openItem,
        error: lastError ?? undefined,
      };
    },
    async loadMore(): Promise<void> {
      await loadMore();
    },
    async refresh(): Promise<void> {
      await refresh();
    },
    openItem(item: BrandFeedItem): void {
      if (destroyed) return;
      openItemInternal(item);
    },
    closeItem(): void {
      closeItem();
    },
    destroy(): void {
      if (destroyed) return;
      destroyed = true;
      if (ioImpl) {
        ioImpl.disconnect();
        ioImpl = null;
      }
      teardownModal();
      if (feedEl.parentNode) feedEl.parentNode.removeChild(feedEl);
    },
  };
}
